import { Injectable } from "@angular/core";

import { IFilterItem } from "../interfaces/geo-audience.types";
import { IndexedDbStore } from "./indexed-db-store";

export const MAP_DRAFT_VERSION = 1;
export const MAP_DRAFT_TTL_MS = 24 * 60 * 60 * 1000;

export interface MapDraftViewport {
    center: [number, number];
    zoom: number;
}

/**
 * What the map needs to rebuild a selection. `filters` is `WorkspaceService.selectedFilters$` as is:
 * areas (custom polygons included), attributes, POIs, CSV POIs and DOOH screens all live there.
 */
export interface MapDraftState {
    dateMode: "month" | "week";
    month: string;
    week: string;
    filters: IFilterItem[];
    brickExpansionRadius: number;
    poiRatio: number;
    viewport: MapDraftViewport | null;
}

export interface MapDraft extends MapDraftState {
    version: number;
    savedAt: number;
    country: string;
}

/**
 * Local, per-browser draft of the Geo Audiences map (plan 25): one draft per user, effective
 * organization (impersonation included) and country, stored in IndexedDB because a selection with
 * polygons, CSV POIs and DOOH screens easily outgrows a URL or `localStorage`.
 *
 * Drafts expire 24 h after the last write and are wiped on sign-out (`clearAll`). The record is
 * stored as a JSON string so anything `selectedFilters` carries in `properties` round-trips the same
 * way it does when the GeoAudience is saved to the API.
 */
@Injectable({ providedIn: "root" })
export class MapDraftService {
    private readonly _store = new IndexedDbStore<string>("geoactivate-map-drafts", "drafts");

    /** `userId:organizationId:country`, or `null` without a signed-in user (nothing is stored then). */
    buildKey(country: string): string | null {
        if (!country) {
            return null;
        }

        let user: { id?: string | number; organization?: { id?: string | number } } | null = null;
        try {
            user = JSON.parse(localStorage.getItem("user") ?? "null");
        } catch {
            return null;
        }
        if (user?.id == null) {
            return null;
        }

        const organizationId = localStorage.getItem("super_admin_organization_id") || user.organization?.id || "-";
        return `${user.id}:${organizationId}:${country}`;
    }

    async save(country: string, state: MapDraftState): Promise<boolean> {
        const key = this.buildKey(country);
        if (!key) {
            return false;
        }

        const draft: MapDraft = { ...state, version: MAP_DRAFT_VERSION, savedAt: Date.now(), country };
        let json: string;
        try {
            json = JSON.stringify(draft);
        } catch (error) {
            console.warn("[MapDraftService] draft is not serializable", error);
            return false;
        }
        return this._store.put(key, json);
    }

    /** The draft for `country`, or `null`. An expired, corrupt or older-version draft is deleted on the way. */
    async load(country: string): Promise<MapDraft | null> {
        const key = this.buildKey(country);
        if (!key) {
            return null;
        }

        const json = await this._store.get(key);
        if (json === undefined) {
            return null;
        }

        const draft = this._parse(json);
        if (!draft || draft.version !== MAP_DRAFT_VERSION || draft.country !== country || Date.now() - draft.savedAt > MAP_DRAFT_TTL_MS) {
            await this._store.delete(key);
            return null;
        }
        return draft;
    }

    async delete(country: string): Promise<boolean> {
        const key = this.buildKey(country);
        return key ? this._store.delete(key) : false;
    }

    /** Every draft of every user in this browser: called on sign-out, account switch and 401. */
    clearAll(): Promise<boolean> {
        return this._store.clear();
    }

    private _parse(json: string): MapDraft | null {
        try {
            const draft = JSON.parse(json) as MapDraft;
            return draft && typeof draft.savedAt === "number" && Array.isArray(draft.filters) ? draft : null;
        } catch {
            return null;
        }
    }
}
